/**
 * Performance Monitoring Utilities
 *
 * Web Vitals tracking, React Query metrics, and Firestore query logging.
 * Performance targets: LCP <2.5s, INP <200ms, CLS <0.1
 *
 * @see Issue #66
 */

import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from 'web-vitals';
import { logger } from '@/lib/logger';

// ========================================
// Types
// ========================================

export interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
  timestamp: number;
}

export interface QueryMetrics {
  queryKey: string;
  duration: number;
  status: 'success' | 'error';
  dataSize?: number;
  timestamp: number;
}

export interface FirestoreMetrics {
  collection: string;
  operation: 'read' | 'write' | 'query';
  duration: number;
  documentCount?: number;
  timestamp: number;
}

export type MetricHandler = (metric: PerformanceMetric) => void;

// ========================================
// Performance Thresholds
// ========================================

export const PERFORMANCE_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint
  INP: { good: 200, poor: 500 }, // Interaction to Next Paint (replaced FID)
  CLS: { good: 0.1, poor: 0.25 }, // Cumulative Layout Shift
  FCP: { good: 1800, poor: 3000 }, // First Contentful Paint
  TTFB: { good: 800, poor: 1800 }, // Time to First Byte
  QUERY: { good: 500, poor: 2000 }, // React Query duration
  FIRESTORE: { good: 300, poor: 1000 }, // Firestore operation duration
} as const;

// ========================================
// Web Vitals Tracking
// ========================================

const metricHandlers: MetricHandler[] = [];

/**
 * Register a handler for performance metrics
 */
export function onMetric(handler: MetricHandler): () => void {
  metricHandlers.push(handler);
  return () => {
    const index = metricHandlers.indexOf(handler);
    if (index > -1) metricHandlers.splice(index, 1);
  };
}

/**
 * Convert web-vitals Metric to our PerformanceMetric format
 */
function convertMetric(metric: Metric): PerformanceMetric {
  return {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType || 'unknown',
    timestamp: Date.now(),
  };
}

/**
 * Emit metric to all registered handlers
 */
function emitMetric(metric: Metric): void {
  const converted = convertMetric(metric);

  // Dev logging
  logger.log(`[Performance] ${converted.name}: ${converted.value.toFixed(2)} (${converted.rating})`);

  // Emit to all handlers
  metricHandlers.forEach((handler) => {
    try {
      handler(converted);
    } catch (error) {
      logger.error('[Performance] Handler error:', error);
    }
  });
}

/**
 * Initialize Web Vitals tracking
 * Call once at app startup
 */
export function initWebVitals(): void {
  onCLS(emitMetric);
  onINP(emitMetric);
  onLCP(emitMetric);
  onFCP(emitMetric);
  onTTFB(emitMetric);

  logger.log('[Performance] Web Vitals tracking initialized');
}

// ========================================
// React Query Performance Tracking
// ========================================

const queryMetricsLog: QueryMetrics[] = [];
const MAX_QUERY_LOG_SIZE = 100;

/**
 * Log React Query performance
 */
export function logQueryMetrics(metrics: QueryMetrics): void {
  queryMetricsLog.push(metrics);

  // Keep log size manageable
  if (queryMetricsLog.length > MAX_QUERY_LOG_SIZE) {
    queryMetricsLog.shift();
  }

  // Log slow queries in development
  if (metrics.duration > PERFORMANCE_THRESHOLDS.QUERY.good) {
    logger.warn(`[Query] Slow query ${metrics.queryKey}: ${metrics.duration}ms (${metrics.status})`);
  }
}

/**
 * Get query performance statistics
 */
export function getQueryStats(): {
  totalQueries: number;
  averageDuration: number;
  slowQueries: number;
  errorRate: number;
} {
  if (queryMetricsLog.length === 0) {
    return { totalQueries: 0, averageDuration: 0, slowQueries: 0, errorRate: 0 };
  }

  const totalQueries = queryMetricsLog.length;
  const totalDuration = queryMetricsLog.reduce((sum, m) => sum + m.duration, 0);
  const slowQueries = queryMetricsLog.filter(
    (m) => m.duration > PERFORMANCE_THRESHOLDS.QUERY.good
  ).length;
  const errorQueries = queryMetricsLog.filter((m) => m.status === 'error').length;

  return {
    totalQueries,
    averageDuration: totalDuration / totalQueries,
    slowQueries,
    errorRate: (errorQueries / totalQueries) * 100,
  };
}

/**
 * Clear query metrics log
 */
export function clearQueryMetrics(): void {
  queryMetricsLog.length = 0;
}

// ========================================
// Firestore Performance Tracking
// ========================================

const firestoreMetricsLog: FirestoreMetrics[] = [];
const MAX_FIRESTORE_LOG_SIZE = 100;

/**
 * Log Firestore operation performance
 */
export function logFirestoreMetrics(metrics: FirestoreMetrics): void {
  firestoreMetricsLog.push(metrics);

  // Keep log size manageable
  if (firestoreMetricsLog.length > MAX_FIRESTORE_LOG_SIZE) {
    firestoreMetricsLog.shift();
  }

  // Log slow operations in development
  if (metrics.duration > PERFORMANCE_THRESHOLDS.FIRESTORE.good) {
    logger.warn(`[Firestore] Slow ${metrics.operation} ${metrics.collection}: ${metrics.duration}ms`);
  }
}

/**
 * Create a timer for measuring Firestore operations
 */
export function createFirestoreTimer(
  collection: string,
  operation: FirestoreMetrics['operation']
): {
  end: (documentCount?: number) => void;
} {
  const startTime = performance.now();

  return {
    end: (documentCount?: number) => {
      const duration = performance.now() - startTime;
      logFirestoreMetrics({
        collection,
        operation,
        duration,
        documentCount,
        timestamp: Date.now(),
      });
    },
  };
}

/**
 * Get Firestore performance statistics
 */
export function getFirestoreStats(): {
  totalOperations: number;
  averageDuration: number;
  slowOperations: number;
  byCollection: Record<string, { count: number; avgDuration: number }>;
} {
  if (firestoreMetricsLog.length === 0) {
    return {
      totalOperations: 0,
      averageDuration: 0,
      slowOperations: 0,
      byCollection: {},
    };
  }

  const totalOperations = firestoreMetricsLog.length;
  const totalDuration = firestoreMetricsLog.reduce((sum, m) => sum + m.duration, 0);
  const slowOperations = firestoreMetricsLog.filter(
    (m) => m.duration > PERFORMANCE_THRESHOLDS.FIRESTORE.good
  ).length;

  // Group by collection
  const byCollection: Record<string, { count: number; totalDuration: number }> = {};
  firestoreMetricsLog.forEach((m) => {
    if (!byCollection[m.collection]) {
      byCollection[m.collection] = { count: 0, totalDuration: 0 };
    }
    byCollection[m.collection].count++;
    byCollection[m.collection].totalDuration += m.duration;
  });

  const byCollectionStats: Record<string, { count: number; avgDuration: number }> = {};
  Object.entries(byCollection).forEach(([collection, stats]) => {
    byCollectionStats[collection] = {
      count: stats.count,
      avgDuration: stats.totalDuration / stats.count,
    };
  });

  return {
    totalOperations,
    averageDuration: totalDuration / totalOperations,
    slowOperations,
    byCollection: byCollectionStats,
  };
}

/**
 * Clear Firestore metrics log
 */
export function clearFirestoreMetrics(): void {
  firestoreMetricsLog.length = 0;
}

// ========================================
// Performance Report
// ========================================

/**
 * Get comprehensive performance report
 */
export function getPerformanceReport(): {
  webVitals: PerformanceMetric[];
  queryStats: ReturnType<typeof getQueryStats>;
  firestoreStats: ReturnType<typeof getFirestoreStats>;
  timestamp: number;
} {
  return {
    webVitals: [], // Web Vitals are reported via handlers, not stored
    queryStats: getQueryStats(),
    firestoreStats: getFirestoreStats(),
    timestamp: Date.now(),
  };
}

// ========================================
// Performance Measurement Utilities
// ========================================

/**
 * Measure execution time of an async function
 */
export async function measureAsync<T>(
  name: string,
  fn: () => Promise<T>
): Promise<{ result: T; duration: number }> {
  const startTime = performance.now();
  const result = await fn();
  const duration = performance.now() - startTime;

  logger.log(`[Measure] ${name}: ${duration.toFixed(2)}ms`);

  return { result, duration };
}

/**
 * Measure execution time of a sync function
 */
export function measureSync<T>(
  name: string,
  fn: () => T
): { result: T; duration: number } {
  const startTime = performance.now();
  const result = fn();
  const duration = performance.now() - startTime;

  logger.log(`[Measure] ${name}: ${duration.toFixed(2)}ms`);

  return { result, duration };
}

/**
 * Create a performance mark and measure
 */
export function createPerformanceMark(name: string): {
  end: () => number;
} {
  const markName = `${name}-start`;
  performance.mark(markName);

  return {
    end: () => {
      const endMarkName = `${name}-end`;
      performance.mark(endMarkName);

      try {
        performance.measure(name, markName, endMarkName);
        const entries = performance.getEntriesByName(name);
        const duration = entries[entries.length - 1]?.duration || 0;

        // Cleanup
        performance.clearMarks(markName);
        performance.clearMarks(endMarkName);
        performance.clearMeasures(name);

        return duration;
      } catch {
        return 0;
      }
    },
  };
}
